import { LayoutGrid, List, Package, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { productThumbUrl } from "@/lib/media.ts";
import { lookupProduct } from "@/lib/scan.ts";
import { useCategoriesLookup } from "@/queries/admin/lookups.ts";
import { useProducts } from "@/queries/products.ts";
import { formatPEN } from "@/lib/utils.ts";
import type { ProductOut } from "@/types/api.ts";

interface Props {
  onSelect: (product: ProductOut) => void;
}

type ViewMode = "grid" | "list";

const VIEW_MODE_KEY = "pos.product_search.view";
const CATEGORY_KEY = "pos.product_search.category_id";

function loadViewMode(): ViewMode {
  if (typeof window === "undefined") return "grid";
  const stored = window.localStorage.getItem(VIEW_MODE_KEY);
  return stored === "list" ? "list" : "grid";
}

function loadCategoryId(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(CATEGORY_KEY);
}

export function ProductSearch({ onSelect }: Props) {
  const [q, setQ] = useState("");
  const [view, setView] = useState<ViewMode>(loadViewMode);
  const [storedCategoryId, setCategoryId] = useState<string | null>(loadCategoryId);
  const categories = useCategoriesLookup();

  // Solo activas en el filtro (las inactivas siguen visibles en admin pero no
  // tienen sentido para el cajero).
  const activeCategories = useMemo(
    () => (categories.data ?? []).filter((c) => c.is_active),
    [categories.data],
  );
  // Ignora la categoría persistida si fue borrada o desactivada (evita quedar
  // con un id fantasma que filtra a "sin resultados").
  const categoryId =
    storedCategoryId !== null &&
    categories.data &&
    !activeCategories.some((c) => c.id === storedCategoryId)
      ? null
      : storedCategoryId;
  const products = useProducts(q, categoryId);

  useEffect(() => {
    window.localStorage.setItem(VIEW_MODE_KEY, view);
  }, [view]);

  useEffect(() => {
    if (categoryId === null) window.localStorage.removeItem(CATEGORY_KEY);
    else window.localStorage.setItem(CATEGORY_KEY, categoryId);
  }, [categoryId]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={async (e) => {
              // Lector de códigos (o SKU tipeado) + Enter: si coincide exacto,
              // va directo al carrito y se limpia para la siguiente lectura.
              if (e.key !== "Enter" || !q.trim()) return;
              e.preventDefault();
              const product = await lookupProduct(q).catch(() => null);
              if (product) {
                onSelect(product);
                setQ("");
              }
            }}
            placeholder="Escanea el código o busca por nombre / SKU"
            className="w-full rounded-xl border border-slate-200 bg-white py-3 pr-4 pl-12 text-base outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
          />
        </div>
        <div className="flex shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white">
          <button
            type="button"
            onClick={() => setView("grid")}
            aria-label="Vista cuadrícula"
            aria-pressed={view === "grid"}
            className={
              "flex items-center justify-center px-3 py-2.5 transition " +
              (view === "grid"
                ? "bg-slate-900 text-white"
                : "text-slate-500 hover:bg-slate-100")
            }
          >
            <LayoutGrid className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => setView("list")}
            aria-label="Vista lista"
            aria-pressed={view === "list"}
            className={
              "flex items-center justify-center px-3 py-2.5 transition " +
              (view === "list"
                ? "bg-slate-900 text-white"
                : "text-slate-500 hover:bg-slate-100")
            }
          >
            <List className="h-5 w-5" />
          </button>
        </div>
      </div>

      {activeCategories.length > 0 && (
        <div className="mt-3 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
          <CategoryChip
            label="Todas"
            active={categoryId === null}
            onClick={() => setCategoryId(null)}
          />
          {activeCategories.map((c) => (
            <CategoryChip
              key={c.id}
              label={c.name}
              active={categoryId === c.id}
              onClick={() => setCategoryId(c.id)}
            />
          ))}
        </div>
      )}

      <div className="mt-3 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white">
        {products.isLoading && (
          <p className="p-4 text-sm text-slate-500">Cargando productos…</p>
        )}
        {products.isError && (
          <p className="p-4 text-sm text-red-600">No se pudo cargar el catálogo.</p>
        )}
        {products.data && products.data.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 p-10 text-slate-500">
            <Package className="h-10 w-10 text-slate-300" />
            <p className="text-sm">{q ? "Sin resultados" : "Empieza a tipear para buscar"}</p>
          </div>
        )}
        {products.data && products.data.length > 0 && (
          view === "grid" ? (
            <ProductGrid products={products.data} onSelect={onSelect} />
          ) : (
            <ProductList products={products.data} onSelect={onSelect} />
          )
        )}
      </div>
    </div>
  );
}

interface ListProps {
  products: ProductOut[];
  onSelect: (product: ProductOut) => void;
}

function ProductList({ products, onSelect }: ListProps) {
  return (
    <ul className="divide-y divide-slate-100">
      {products.map((p) => {
        const thumb = productThumbUrl(p.image_path);
        return (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => onSelect(p)}
              className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 active:bg-slate-100"
            >
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                {thumb ? (
                  <img
                    src={thumb}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-slate-300">
                    <Package className="h-5 w-5" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">{p.name}</p>
                <p className="text-xs text-slate-500">
                  SKU {p.sku}
                  {p.barcode ? ` · ${p.barcode}` : ""}
                  {p.igv_exempt ? " · Exonerado IGV" : ""}
                </p>
              </div>
              <span className="text-sm font-semibold tabular-nums text-slate-900">
                {formatPEN(p.base_price)}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function ProductGrid({ products, onSelect }: ListProps) {
  return (
    <ul className="grid grid-cols-2 gap-3 p-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
      {products.map((p) => {
        const thumb = productThumbUrl(p.image_path);
        return (
          <li key={p.id}>
            <button
              type="button"
              onClick={() => onSelect(p)}
              className="group flex w-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white text-left transition hover:border-slate-400 hover:shadow-md active:scale-[0.98]"
            >
              <div
                className="relative w-full overflow-hidden bg-slate-50"
                style={{ paddingBottom: "100%" }}
              >
                {thumb ? (
                  <img
                    src={thumb}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 block h-full w-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-slate-300">
                    <Package className="h-12 w-12" />
                  </div>
                )}
                {p.igv_exempt && (
                  <span className="absolute top-1.5 right-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                    Exonerado
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1 p-2.5">
                <p className="line-clamp-2 h-[2.25rem] overflow-hidden text-sm font-medium leading-tight text-slate-900">
                  {p.name}
                </p>
                <p className="h-[0.875rem] truncate font-mono text-[10px] text-slate-400">
                  {p.sku}
                </p>
                <p className="text-base font-semibold tabular-nums text-slate-900">
                  {formatPEN(p.base_price)}
                </p>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

interface CategoryChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

function CategoryChip({ label, active, onClick }: CategoryChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        "shrink-0 rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap transition " +
        (active
          ? "bg-slate-900 text-white"
          : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-100")
      }
    >
      {label}
    </button>
  );
}
